import { Suspense, useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { Button } from "@zapier/design-system-beta";
import {
  isExecutingZslAtom,
  upgradeBannerPropsAsyncAtom,
  clearCanvasAtom,
  handleUseTemplateCommand,
  templateNameAtom,
  showCanvasTemplateWidgetAtom,
  numberOfUniqueTableNodesAtom,
  numberOfUniqueInterfaceNodesAtom,
  numberOfUniqueChatbotNodesAtom,
} from "@zapier/canvas-state";

import { useCenterAndZoomOnNode } from "../../hooks/useCenterAndZoomOnNode";
import { UpgradeBanner } from "../UpgradeBanner";
import { BannerWrapper } from "../BannerWrapper";
import { LoadingTemplateBanner } from "../LoadingTemplateBanner";

type Props = {
  resetToBlank: () => void;
};

/** This banner is being used when you click on a template from template widget */

function TemplateExecutionBanner({ resetToBlank }: Props) {
  const isZslExecuting = useAtomValue(isExecutingZslAtom);
  const templateName = useAtomValue(templateNameAtom);
  const handleUseTemplate = useSetAtom(handleUseTemplateCommand);

  const title = `You are previewing the template '${templateName}'`;
  const description =
    "Using this template will create the Zapier assets displayed below.";

  return (
    <BannerWrapper title={title} description={description}>
      <Button variant="tertiary" size="medium" onPress={resetToBlank}>
        Start blank
      </Button>
      <Button isLoading={isZslExecuting} onPress={handleUseTemplate}>
        Use this template
      </Button>
    </BannerWrapper>
  );
}

function SuspendedBanner({ resetToBlank }: Props) {
  const upgradeBannerProps = useAtomValue(upgradeBannerPropsAsyncAtom);

  if (upgradeBannerProps === null) {
    return <TemplateExecutionBanner resetToBlank={resetToBlank} />;
  }

  return (
    <UpgradeBanner {...upgradeBannerProps} location="Template banner">
      <Button variant="tertiary" onPress={resetToBlank}>
        Start blank
      </Button>
    </UpgradeBanner>
  );
}

export function TemplateBanner() {
  const { centerAndZoomOnNode } = useCenterAndZoomOnNode();
  const numberOfTableNodes = useAtomValue(numberOfUniqueTableNodesAtom);
  const numberOfInterfaceNodes = useAtomValue(numberOfUniqueInterfaceNodesAtom);
  const numberOfChatbotNodes = useAtomValue(numberOfUniqueChatbotNodesAtom);
  const shouldShowSuspendedBanner =
    numberOfTableNodes + numberOfInterfaceNodes + numberOfChatbotNodes > 0;

  const resetToBlank = useAtomCallback(
    useCallback(
      async (_get, set) => {
        set(showCanvasTemplateWidgetAtom, false);
        await set(clearCanvasAtom, centerAndZoomOnNode);
      },
      [centerAndZoomOnNode],
    ),
  );

  if (shouldShowSuspendedBanner) {
    return (
      <Suspense fallback={<LoadingTemplateBanner />}>
        <SuspendedBanner resetToBlank={resetToBlank} />
      </Suspense>
    );
  }

  return <TemplateExecutionBanner resetToBlank={resetToBlank} />;
}
